/**
 * Actions for the device handoff feature.
 */
import { HANDOFF_DEVICE_FOUND, HANDOFF_DEVICE_LOST, HANDOFF_SERVER_DEVICES_UPDATED, HANDOFF_PROXIMITY_SAMPLE, HANDOFF_STARTED, HANDOFF_PROGRESS_UPDATE, HANDOFF_COMPLETED, HANDOFF_FAILED, HANDOFF_RECEIVED, HANDOFF_ACCEPTED, HANDOFF_DECLINED, HANDOFF_INTERPRETER_NOTIFY, HANDOFF_INTERPRETER_COMPLETE } from './actionTypes';
import { handoffService } from './DeviceHandoffService';
import { appNavigate } from '../app/actions';
import { isHandoffVideoReady } from './mediaReadiness';
import { setPersistentItem } from '../vrs-auth/storage';
/**
 * Start BLE scanning for companion devices.
 */
export function startHandoffScanning() {
    return () => {
        handoffService.startScanning();
    };
}
/**
 * Stop BLE scanning.
 */
export function stopHandoffScanning() {
    return () => {
        handoffService.stopScanning();
    };
}
/**
 * Initiate a handoff to a companion device.
 */
export function initiateHandoff(companionDevice) {
    return async (dispatch) => {
        const progress = await handoffService.initiateHandoff(companionDevice);
        if (progress) {
            dispatch(handoffProgressUpdate(progress));
        }
    };
}
/**
 * Ask the current browser/mobile runtime for a companion device, then start handoff.
 */
export function requestCompanionHandoff() {
    return async (dispatch) => {
        let device = await handoffService.requestCompanionDevice();
        if (!device) {
            device = {
                id: `transfer-link-${Date.now()}`,
                name: 'Transfer link',
                rssi: 0
            };
        }
        const progress = await handoffService.initiateHandoff(device);
        if (progress) {
            dispatch(handoffProgressUpdate(progress));
        }
    };
}
/**
 * Accept a received handoff (receiving device).
 */
export function acceptHandoff(token) {
    return async (dispatch) => {
        const result = await handoffService.acceptHandoff(token);
        if (result && 'roomName' in result && typeof result.roomName === 'string') {
            if ('activeCall' in result && result.activeCall) {
                setPersistentItem('vrs_active_call', JSON.stringify({
                    ...result.activeCall,
                    roomName: result.roomName,
                    startedAt: result.activeCall.startedAt || Date.now()
                }));
            }
            dispatch(appNavigate(result.roomName, {
                hidePrejoin: true,
                // A transfer is not a fresh call preference decision. The
                // target must publish video before the source is released.
                startWithVideoMuted: false
            }));
        }
    };
}
/**
 * Decline a received handoff (receiving device).
 */
export function declineHandoff(token) {
    return async () => {
        await handoffService.declineHandoff(token);
    };
}
/**
 * Confirm that the new device's video track is established.
 */
export function confirmHandoffTrackEstablished(roomName, hasLocalVideoTrack) {
    return (_dispatch, getState) => handoffService.confirmTrackEstablished(roomName, () => hasLocalVideoTrack && isHandoffVideoReady(getState(), roomName));
}
// ---- Pure action creators (called by middleware / service events) ----
export function handoffDeviceFound(device) {
    return {
        type: HANDOFF_DEVICE_FOUND,
        device
    };
}
export function handoffDeviceLost(deviceId) {
    return {
        type: HANDOFF_DEVICE_LOST,
        deviceId
    };
}
export function handoffServerDevicesUpdated(devices) {
    return {
        type: HANDOFF_SERVER_DEVICES_UPDATED,
        devices
    };
}
export function handoffProximitySample(device) {
    return {
        type: HANDOFF_PROXIMITY_SAMPLE,
        device
    };
}
export function handoffStarted(companionDevice) {
    return {
        type: HANDOFF_STARTED,
        companionDevice
    };
}
export function handoffProgressUpdate(progress) {
    return {
        type: HANDOFF_PROGRESS_UPDATE,
        progress
    };
}
export function handoffCompleted(roomName) {
    return {
        type: HANDOFF_COMPLETED,
        roomName
    };
}
export function handoffFailed(error) {
    return {
        type: HANDOFF_FAILED,
        error
    };
}
export function handoffReceived(token) {
    return {
        type: HANDOFF_RECEIVED,
        token
    };
}
export function handoffAccepted(data) {
    return {
        type: HANDOFF_ACCEPTED,
        data
    };
}
export function handoffDeclined(token) {
    return {
        type: HANDOFF_DECLINED,
        token
    };
}
export function handoffInterpreterNotify(data) {
    return {
        type: HANDOFF_INTERPRETER_NOTIFY,
        data
    };
}
export function handoffInterpreterComplete(data) {
    return {
        type: HANDOFF_INTERPRETER_COMPLETE,
        data
    };
}
